import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LogIn, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Logo from "../assets/chillaos.png";

export const ALLOWED_EMAILS = [
  "hector.diaz@pucp.edu.pe",
  "hector.19.ares@gmail.com"
];

export default function Navbar() {
  const { user, signInGoogle, signOutUser } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    if (user && !ALLOWED_EMAILS.includes(user.email)) {
      alert("Access denied. This archive is restricted to authorized project administrators only.");
      signOutUser();
    }
  }, [user, signOutUser]);

  const isAuthorizedAdmin = user && ALLOWED_EMAILS.includes(user.email);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 w-full bg-[#ECE3D0] border-b border-[#3a2a1c]/25 transition-colors duration-300">
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-5 flex items-center justify-between">
         
        {/* Logo & Masthead Section */}
        <Link to="/" onClick={closeMobileMenu} className="flex items-center gap-4 group">
          <img
            className="w-7 h-auto opacity-85 group-hover:opacity-100 transition-opacity"
            src={Logo}
            alt="Chillaos Logo"
          />
          <span className="font-serif text-lg md:text-xl text-[#1a1410] tracking-tight">
            <span className="italic">Chillaos</span>{' '}
            <span className="text-[#3a2a1c]/70 text-base">Project</span>
          </span>
        </Link>
        
        {/* Desktop Navigation Links & Auth Buttons */}
        <nav className="hidden md:flex items-center gap-8">
          {!isHome && (
            <NavLink 
              to="/" 
              className={({isActive}) => 
                `font-mono text-[10px] uppercase tracking-[0.35em] transition-all pb-1 border-b ${
                  isActive 
                    ? 'text-[#6B2C2C] border-[#6B2C2C]/40' 
                    : 'text-[#3a2a1c]/60 border-transparent hover:text-[#1a1410] hover:border-[#3a2a1c]/30'
                }`
              }
            >
              Home
            </NavLink>
          )}
          
          {isAuthorizedAdmin && (
            <NavLink 
              to="/dashboard" 
              className={({isActive}) => 
                `font-mono text-[10px] uppercase tracking-[0.35em] transition-all pb-1 border-b ${
                  isActive 
                    ? 'text-[#6B2C2C] border-[#6B2C2C]/40' 
                    : 'text-[#3a2a1c]/60 border-transparent hover:text-[#1a1410] hover:border-[#3a2a1c]/30'
                }`
              }
            >
              Archive
            </NavLink>
          )}
          {isAuthorizedAdmin && (
            <NavLink 
              to="/admin" 
              className={({isActive}) => 
                `font-mono text-[10px] uppercase tracking-[0.35em] transition-all pb-1 border-b ${
                  isActive 
                    ? 'text-[#6B2C2C] border-[#6B2C2C]/40' 
                    : 'text-[#3a2a1c]/60 border-transparent hover:text-[#1a1410] hover:border-[#3a2a1c]/30'
                }`
              }
            >
              Admin
            </NavLink>
          )}

          {/* Archival divider */}
          <span className="h-4 w-px bg-[#3a2a1c]/25" />

          {isAuthorizedAdmin ? (
            <button 
              onClick={signOutUser} 
              className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#3a2a1c]/60 hover:text-[#6B2C2C] transition-colors"
            >
              <LogOut className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100" /> 
              <span>Depart</span>
            </button>
          ) : (
            <button 
              onClick={signInGoogle} 
              className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#3a2a1c]/60 hover:text-[#1a1410] transition-colors"
            >
              <LogIn className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100" /> 
              <span>Access</span>
            </button>
          )}
        </nav>

        {/* Mobile Menu Toggle Button */}
        <button 
          className="md:hidden p-2 -mr-2 text-[#3a2a1c]/70 hover:text-[#1a1410] transition-colors"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X className="h-5 w-5 stroke-[1.5]" /> : <Menu className="h-5 w-5 stroke-[1.5]" />}
        </button>
      </div>

      {/* Mobile Navigation Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-[#ECE3D0] border-b border-[#3a2a1c]/25 shadow-2xl py-8 px-8 flex flex-col gap-8 overflow-hidden">
          
          {/* Subtle paper texture matching the landing page */}
          <div
            className="pointer-events-none absolute inset-0 z-0 opacity-[0.15] mix-blend-multiply"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 320 320' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.78' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.18 0 0 0 0 0.12 0 0 0 0 0.06 0 0 0 0.55 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
          />

          <div className="relative z-10 flex flex-col gap-6">
            {!isHome && (
              <NavLink 
                to="/" 
                onClick={closeMobileMenu}
                className={({isActive}) => 
                  `font-serif text-3xl tracking-tight ${
                    isActive 
                      ? 'italic text-[#6B2C2C]' 
                      : 'text-[#1a1410] hover:italic hover:text-[#3a2a1c] transition-all'
                  }`
                }
              >
                Home 
              </NavLink>
            )}
            
            {isAuthorizedAdmin && (
              <NavLink 
                to="/dashboard" 
                onClick={closeMobileMenu}
                className={({isActive}) => 
                  `font-serif text-3xl tracking-tight ${
                    isActive 
                      ? 'italic text-[#6B2C2C]' 
                      : 'text-[#1a1410] hover:italic hover:text-[#3a2a1c] transition-all'
                  }`
                }
              >
                Archive
              </NavLink>
            )}
            {isAuthorizedAdmin && (
              <NavLink 
                to="/admin" 
                onClick={closeMobileMenu}
                className={({isActive}) => 
                  `font-serif text-3xl tracking-tight ${
                    isActive 
                      ? 'italic text-[#6B2C2C]' 
                      : 'text-[#1a1410] hover:italic hover:text-[#3a2a1c] transition-all'
                  }`
                }
              >
                Admin
              </NavLink>
            )}

            <div className="pt-8 border-t border-[#3a2a1c]/15 mt-2">
              {isAuthorizedAdmin ? (
                <button 
                  onClick={() => { signOutUser(); closeMobileMenu(); }} 
                  className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-[#3a2a1c]/60 hover:text-[#6B2C2C] transition-colors"
                >
                  <LogOut className="h-4 w-4 stroke-[1.5]" /> Depart
                </button>
              ) : (
                <button 
                  onClick={() => { signInGoogle(); closeMobileMenu(); }} 
                  className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-[#3a2a1c]/60 hover:text-[#1a1410] transition-colors"
                >
                  <LogIn className="h-4 w-4 stroke-[1.5]" /> Access
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}